import React, { useState } from 'react';
import { Routes, Route, useNavigate, useLocation } from 'react-router-dom';
import './App.css';
import { AuthProvider } from './contexts/AuthContext';
import ProtectedRoute from './components/ProtectedRoute';
import Header from './components/Layout/Header';
import Sidebar from './components/Layout/Sidebar';
import DashboardStats from './components/Dashboard/DashboardStats';
import TestingPanel from './components/Dashboard/TestingPanel';
import LeadsTable from './components/Leads/LeadsTable';
import KnowledgeBase from './components/Knowledge/KnowledgeBase';
import ResourceManager from './components/Resources/ResourceManager';
import { AnalyticsDashboard } from './components/Analytics';
import ConversationDetail from './components/Analytics/ConversationDetail';
import { SegmentDashboard, SegmentCreator, SegmentLeadView } from './components/Segments';
import WidgetSettings from './components/Widget/WidgetSettings';
import CampaignDashboard from './components/Campaigns/CampaignDashboard';
import ContentGenerator from './components/Campaigns/ContentGenerator';
import CampaignCreator from './components/Campaigns/CampaignCreator';
import CampaignEditor from './components/Campaigns/CampaignEditor';
import PreviewPage from './components/Campaigns/PreviewPage';
import TemplateManagement from './pages/TemplateManagement';
import ContactForm from './pages/ContactForm';
import NewsletterForm from './pages/NewsletterForm';
import ContactSuccess from './pages/ContactSuccess';
import EmailNotifications from './pages/EmailNotifications';
import QuizLandingPage from './pages/QuizLandingPage';
import QuizFlow from './pages/QuizFlow';
import QuizResultsPage from './pages/QuizResultsPage';
import { Toaster } from 'react-hot-toast';

// Dashboard Page Component
const DashboardPage = () => {
  return (
    <div>
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-gray-900">Dashboard Overview</h2>
        <p className="text-gray-600">
          Welcome to your Private School Lead Generation Platform
        </p>
      </div>
      <TestingPanel />
      <DashboardStats />
    </div>
  );
};

function App() {
  const navigate = useNavigate();
  const location = useLocation();
  
  // Determine current view from URL path
  const currentView = location.pathname === '/' ? 'dashboard' : location.pathname.slice(1);
  
  // Segment management state
  const [showSegmentCreator, setShowSegmentCreator] = useState(false);
  const [editingSegment, setEditingSegment] = useState<any>(null);
  const [showSegmentLeads, setShowSegmentLeads] = useState<any>(null);
  const [segmentRefreshTrigger, setSegmentRefreshTrigger] = useState(0);

  // Campaign management state
  const [showContentGenerator, setShowContentGenerator] = useState(false);
  const [showCampaignCreator, setShowCampaignCreator] = useState(false);
  const [editingCampaign, setEditingCampaign] = useState<any>(null);
  const [campaignRefreshTrigger, setCampaignRefreshTrigger] = useState(0);

  // Segment handlers
  const handleSaveSegment = async (segmentData: any) => {
    try {
      const segmentService = (await import('./services/segmentService')).default;
      
      if (editingSegment) {
        await segmentService.updateSegment(editingSegment.id, segmentData);
      } else {
        await segmentService.createSegment(segmentData);
      }
      
      setShowSegmentCreator(false);
      setEditingSegment(null);
      
      // Trigger refresh of SegmentDashboard
      setSegmentRefreshTrigger(prev => prev + 1);
    } catch (error) {
      console.error('Error saving segment:', error);
      window.alert('Failed to save segment. Please try again.');
    }
  };

  // Campaign handlers
  const handleContentGenerated = (results: any[]) => {
    console.log('Content generated:', results);
    // Show success message
    if (results.length > 0) {
      window.alert(`Successfully generated ${results.length} campaigns!`);
    }
    // Refresh campaign dashboard
    setCampaignRefreshTrigger(prev => prev + 1);
  };

  const handleSaveCampaign = async (result: any) => {
    try {
      console.log('Campaign saved:', result);
      
      setShowCampaignCreator(false);
      setEditingCampaign(null);
      
      // Show success message
      if (editingCampaign) {
        window.alert('Campaign updated successfully!');
      } else {
        const { campaigns_created } = result;
        if (campaigns_created === 1) {
          window.alert('Campaign created successfully!');
        } else {
          window.alert(`Successfully created ${campaigns_created} campaigns!`);
        }
      }
      
      // Trigger refresh of CampaignDashboard
      setCampaignRefreshTrigger(prev => prev + 1);
    } catch (error) {
      console.error('Error saving campaign:', error);
      window.alert('Failed to save campaign. Please try again.');
    }
  };

  // Campaigns Page Component with Modals
  const CampaignsPage = () => {
    return (
      <>
        <CampaignDashboard
          onCreateCampaign={() => {
            setEditingCampaign(null);
            setShowCampaignCreator(true);
          }}
          onEditCampaign={(campaign) => {
            setEditingCampaign(campaign);
            setShowCampaignCreator(true);
          }}
          onGenerateContent={() => setShowContentGenerator(true)}
          refreshTrigger={campaignRefreshTrigger}
        />
        {showContentGenerator && (
          <ContentGenerator
            onClose={() => setShowContentGenerator(false)}
            onGenerated={handleContentGenerated}
          />
        )}
        {showCampaignCreator && (
          editingCampaign ? (
            <CampaignEditor
              campaign={editingCampaign}
              onSave={handleSaveCampaign}
              onCancel={() => {
                setShowCampaignCreator(false);
                setEditingCampaign(null);
              }}
            />
          ) : (
            <CampaignCreator
              onSave={handleSaveCampaign}
              onCancel={() => setShowCampaignCreator(false)}
            />
          )
        )}
      </>
    );
  };

  // Segments Page Component
  const SegmentsPage = () => {
    return (
      <SegmentDashboard
        onCreateSegment={() => {
          setEditingSegment(null);
          setShowSegmentCreator(true);
        }}
        onEditSegment={(segment) => {
          setEditingSegment(segment);
          setShowSegmentCreator(true);
        }}
        onViewSegment={(segment) => {
          setShowSegmentLeads(segment);
        }}
        refreshTrigger={segmentRefreshTrigger}
      />
    );
  };

  return (
    <AuthProvider>
      <div className="App min-h-screen bg-gray-100">
        <Toaster 
          position="top-right"
          toastOptions={{
            duration: 4000,
            style: {
              background: '#363636',
              color: '#fff',
            },
            success: {
              style: {
                background: '#10b981',
              },
            },
            error: {
              style: {
                background: '#ef4444',
              },
            },
          }}
        />
        <Routes>
          {/* Public Routes - No Authentication Required */}
          <Route path="/preview/:token" element={<PreviewPage />} />
          <Route path="/contact" element={<ContactForm />} />
          <Route path="/newsletter" element={<NewsletterForm />} />
          <Route path="/contact/success" element={<ContactSuccess />} />

          {/* Quiz System Routes - Public */}
          <Route path="/quiz" element={<QuizLandingPage />} />
          <Route path="/quiz/assessment" element={<QuizFlow />} />
          <Route path="/quiz/results/:token" element={<QuizResultsPage />} />

          {/* Protected Routes - Authentication Required */}
          <Route path="/*" element={
            <ProtectedRoute>
              <Header />
              <div className="flex">
                <Sidebar currentView={currentView} onViewChange={(view) => navigate(`/${view === 'dashboard' ? '' : view}`)} />
                <main className="flex-1 py-6 px-4 sm:px-6 lg:px-8">
                  <Routes>
                    <Route path="/" element={<DashboardPage />} />
                    <Route path="/leads" element={<LeadsTable />} />
                    <Route path="/analytics" element={<AnalyticsDashboard />} />
                    <Route path="/analytics/conversation/:conversationId" element={<ConversationDetail />} />
                    <Route path="/knowledge" element={<KnowledgeBase />} />
                    <Route path="/segments" element={<SegmentsPage />} />
                    <Route path="/resources" element={<ResourceManager />} />
                    <Route path="/campaigns" element={<CampaignsPage />} />
                    <Route path="/templates" element={<TemplateManagement />} />
                    <Route path="/widget" element={<WidgetSettings />} />
                    <Route path="/email-notifications" element={<EmailNotifications />} />
                  </Routes>
                </main>
              </div>
            </ProtectedRoute>
          } />
        </Routes>

        {/* Segment Creator Modal */}
        {showSegmentCreator && (
          <SegmentCreator
            segment={editingSegment}
            onSave={handleSaveSegment}
            onCancel={() => {
              setShowSegmentCreator(false);
              setEditingSegment(null);
            }}
          />
        )}

        {/* Segment Lead View Modal */}
        {showSegmentLeads && (
          <SegmentLeadView
            segment={showSegmentLeads}
            onClose={() => setShowSegmentLeads(null)}
            onSegmentUpdated={() => setSegmentRefreshTrigger(prev => prev + 1)}
          />
        )}
      </div>
    </AuthProvider>
  );
}

export default App;